import { ecoOrderPersonName, ecoOrderPersonTooltip } from '../../../../../shared/widgets/eco/eco-order-person';
import { Component, ChangeDetectionStrategy, computed, inject, signal, effect, input, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { WidgetsModule } from '../../../../../shared/widgets.module';
import { ResumeDataService } from '../../services/resume-data.service';
import { EcoOrdersService, EcoServiceOrderFilters } from '../../../../../services/eco/eco-orders.service';
import { IEcoServiceOrder } from '../../../../../interfaces/eco/eco-service-order.interface';
import { ECO_SERVICE_ORDER_STATUSES, ECO_EQUIPMENT_TYPES } from '../../../../../constants/eco.constants';
import { DateFilterValue } from '../../../../../shared/widgets/document-viewer/components/date-filter/date-filter.component';
import { EcoOrderInfoModalComponent } from '../../../../../shared/widgets/eco/eco-order-info-modal/eco-order-info-modal.component';

import {
  createActionColumn,
  createPlainColumn
} from '../../../../../shared/components/kite-grid/column-utils/column-utils';

@Component({
  selector: 'app-customer-eco-orders-tab',
  standalone: true,
  imports: [CommonModule, WidgetsModule],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './customer-eco-orders-tab.component.html',
  styleUrls: ['./customer-eco-orders-tab.component.scss']
})
export class CustomerEcoOrdersTabComponent {
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);
  private readonly ordersService = inject(EcoOrdersService);
  private readonly datePipe = inject(DatePipe);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalService = inject(NgbModal);
  readonly resumeData = inject(ResumeDataService);

  readonly clientId = input<string>('');
  readonly isVisible = input.required<boolean>();

  readonly searchTerm = signal<string>('');
  readonly fromDate = signal<string>('');
  readonly toDate = signal<string>('');
  readonly orders = signal<IEcoServiceOrder[]>([]);
  readonly total = signal<number>(0);
  readonly loading = signal<boolean>(false);
  readonly page = signal<number>(1);
  readonly pageSize = 10;

  readonly effectiveClientId = computed(() => {
    return this.clientId() || this.resumeData.clientID() || '';
  });

  readonly gridOptions = {
    overlayNoRowsTemplate: '<span class="ag-overlay-no-rows-center text-muted font-weight-medium">No ECO service orders found</span>',
    localeText: {
      noRowsToShow: 'No ECO service orders found'
    }
  };

  private lastLoadedSignature = '';

  columnDefs: any[] = [];

  constructor() {
    this.columnDefs = [
      createActionColumn({
        width: 60,
        buttons: [
          {
            icon: 'fa-eye',
            title: 'View order details',
            ariaLabel: 'view order details',
            classes: 'view-order-btn'
          }
        ]
      }),
      createPlainColumn({
        field: 'ident_order',
        headerName: 'Order #',
        flex: 1,
        cellClass: 'ag-cell-center'
      }),
      {
        headerName: 'Status',
        field: 'status',
        width: 170,
        cellRenderer: (params: any) => {
          const status = params.value ?? '';
          const match = ECO_SERVICE_ORDER_STATUSES.find(s => s.code === status);
          const name = match ? match.name : status;
          const color = match ? match.color : '#6b7280';
          return `
            <div style="display:flex;align-items:center;gap:6px;line-height:1.2;">
              <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background-color:${color};flex-shrink:0;"></span>
              <span style="font-size:12px;font-weight:500;">${name}</span>
            </div>
          `;
        }
      },
      {
        headerName: 'Equipment / Location',
        field: 'equipment',
        flex: 1.4,
        valueGetter: (params: any) => {
          const eq = params.data?.equipment;
          if (!eq) return 'N/A';
          const match = ECO_EQUIPMENT_TYPES.find(t => t.code === eq.type);
          const typeName = match ? match.name : (eq.type || 'N/A');
          const loc = eq.location_type ? ` (${eq.location_type})` : '';
          return `${typeName}${loc}`;
        }
      },
      {
        headerName: 'Scheduled Date',
        field: 'scheduled_date',
        flex: 1.2,
        cellClass: 'ag-cell-center',
        valueGetter: (params: any) => {
          const order = params.data;
          if (order?.scheduled_date) {
            return this.datePipe.transform(order.scheduled_date, 'MMM d, y') || order.scheduled_date;
          }
          if (order?.scheduled_month) {
            return `Month: ${order.scheduled_month}`;
          }
          return 'Unscheduled';
        }
      },
      {
        headerName: 'Completed Date',
        field: 'completed_date',
        flex: 1.2,
        cellClass: 'ag-cell-center',
        valueGetter: (params: any) => {
          const d = params.data?.completed_date;
          return d ? (this.datePipe.transform(d, 'MMM d, y h:mm a') || d) : '-';
        }
      },
      {
        headerName: 'Assigned / Processed by',
        field: 'assigned_to_name',
        flex: 1.2,
        valueGetter: (params: any) => ecoOrderPersonName(params.data, 'Unassigned'),
        tooltipValueGetter: (params: any) => ecoOrderPersonTooltip(params.data)
      }
    ];

    effect(() => {
      const visible = this.isVisible();
      const cid = this.effectiveClientId();
      const page = this.page();
      const search = this.searchTerm();
      const from = this.fromDate();
      const to = this.toDate();

      if (visible && cid) {
        const signature = `${cid}|${page}|${search}|${from}|${to}`;
        if (signature !== this.lastLoadedSignature) {
          this.lastLoadedSignature = signature;
          this.fetchOrders();
        }
      }
    });
  }

  onSearch(term: string): void {
    this.searchTerm.set(term);
    this.page.set(1);
    this.lastLoadedSignature = '';
    this.fetchOrders();
  }

  onDateFilterChange(value: DateFilterValue | null): void {
    if (!value || value.type === 'none') {
      this.fromDate.set('');
      this.toDate.set('');
    } else {
      this.fromDate.set(value.startDate ? new Date(value.startDate).toISOString() : '');
      this.toDate.set(value.endDate ? new Date(value.endDate).toISOString() : '');
    }
    this.page.set(1);
    this.lastLoadedSignature = '';
    this.fetchOrders();
  }

  onPageChange(page: number): void {
    this.page.set(page);
    this.lastLoadedSignature = '';
    this.fetchOrders();
  }

  onCellClicked(event: any): void {
    if (!event?.data?._id) return;
    this.openOrderDetail(event.data._id);
  }

  openOrderDetail(orderId: string): void {
    const modalRef = this.modalService.open(EcoOrderInfoModalComponent, {
      size: 'xl',
      centered: true,
      scrollable: true
    });

    const ref = modalRef as any;
    const componentRef = ref.componentRef || ref._contentRef?.componentRef;
    if (componentRef) {
      componentRef.setInput('orderId', orderId);
    } else {
      modalRef.componentInstance.orderId = orderId;
      modalRef.componentInstance.orderIdValue = orderId;
    }

    modalRef.result.then((res: any) => {
      if (res?.action === 'refresh') {
        this.lastLoadedSignature = '';
        this.fetchOrders();
      }
    }).catch(() => {});
  }

  private fetchOrders(): void {
    const cid = this.effectiveClientId();
    if (!cid) return;

    this.loading.set(true);

    const filters: EcoServiceOrderFilters = {
      clientId: cid,
      sortBy: 'scheduled_date',
      sortOrder: -1
    };

    if (this.searchTerm().trim()) {
      filters.search = this.searchTerm().trim();
    }

    if (this.fromDate()) {
      filters.from = this.fromDate();
    }

    if (this.toDate()) {
      filters.to = this.toDate();
    }

    this.ordersService.list(filters, this.page(), this.pageSize).subscribe({
      next: (res) => {
        this.orders.set(res.data?.orders ?? []);
        this.total.set(res.data?.total ?? 0);
        this.loading.set(false);
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.toastr.error(err?.error?.msg || 'Error loading ECO orders', 'Error');
        this.loading.set(false);
        this.cdr.markForCheck();
      }
    });
  }
}
